import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Heading,
  Text,
  VStack,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { GreshamHeader } from "../components/GreshamHeader";
import { GreshamFooter } from "../components/GreshamFooter";

const Contact: React.FC = () => {
  const textColor = useColorModeValue("gray.800", "white");
  const parallaxRef = useRef<HTMLDivElement>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (parallaxRef.current) {
        const scrolled = window.pageYOffset;
        const rate = scrolled * -0.5;
        parallaxRef.current.style.transform = `translateY(${rate}px)`;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: ""
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    // Allow only phone-like characters for the phone field
    if (name === "phone") {
      const cleaned = value.replace(/[^0-9+\s-]/g, "");
      setFormData(prev => ({
        ...prev,
        phone: cleaned,
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const apiUrl = process.env.REACT_APP_FORM_API_URL;

    if (!apiUrl) {
      toast({
        title: "Form API not configured",
        description: "Please configure REACT_APP_FORM_API_URL.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          formType: "contact",
          ...formData,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Contact form submission error", {
          status: response.status,
          statusText: response.statusText,
          body: errorText,
        });

        let errorMessage = "Something went wrong while sending your message. Please try again.";
        try {
          const parsed = JSON.parse(errorText);
          if (parsed && typeof parsed.message === "string") {
            errorMessage = parsed.message;
          }
        } catch {
          // ignore JSON parse errors, fall back to default message
        }

        throw new Error(errorMessage);
      }

      // Clear the form and show success modal
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        message: "",
      });
      onOpen();
    } catch (error: any) {
      console.error("Contact form submission failed", error);
      toast({
        title: "Submission failed",
        description: error?.message || "Something went wrong while sending your message. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      minHeight="100vh"
      bg="white"
      css={{
        overscrollBehavior: "none",
        "-webkit-overflow-scrolling": "touch"
      }}
    >
      <GreshamHeader />

      {/* Hero Section with Background Image */}
      <Box
        as="section"
        w="full"
        position="relative"
        h="100vh"
        minH="600px"
        overflow="hidden"
        clipPath="inset(0)"
      >
        {/* Background Image Container with Parallax */}
        <Box
          ref={parallaxRef}
          position="fixed"
          top="0"
          left="0"
          width="100vw"
          minWidth="100vw"
          height="120vh"
          backgroundImage="url('/background/emilia-igartua-bZh01rUqcXU-unsplash.jpg')"
          backgroundSize="cover"
          backgroundPosition="center center"
          backgroundRepeat="no-repeat"
          backgroundAttachment="fixed"
          filter="brightness(0.7)"
          willChange="transform"
          zIndex={-1}
          css={{
            transform: "translateX(0) !important",
            right: "auto !important"
          }}
        />

        {/* Text Container - Overlay on background for mobile, positioned for desktop */}
        <Box
          position={{ base: "absolute", lg: "absolute" }}
          top={{ base: "50%", lg: 0 }}
          left={{ base: 0, lg: 0 }}
          w={{ base: "full", lg: "70%" }}
          h={{ base: "auto", lg: "calc(120px + 300px)", xl: "calc(140px + 300px)", "2xl": "calc(160px + 300px)" }}
          display="flex"
          alignItems={{ base: "center", lg: "flex-end" }}
          justifyContent={{ base: "center", lg: "flex-start" }}
          zIndex={100}
          bg={{ base: "transparent", lg: "transparent" }}
          mb={{ base: 0, lg: 0 }}
          order={{ base: 1, lg: 0 }}
          pt={{ base: 0, md: 0, lg: "120px", xl: "140px", "2xl": "160px" }}
          pb={{ base: 0, md: 0, lg: 0 }}
          px={{ base: 6, sm: 8, md: 12, lg: 16 }}
          transform={{ base: "translateY(-50%)", lg: "none" }}
        >
          <VStack spacing={3} align="flex-start" maxW={{ base: "90%", sm: "80%", md: "700px", lg: "800px" }} ml={{ base: "auto", lg: "10%" }} mr="auto" textAlign="left" justify="center">
            {/* Space placeholder like homepage */}
            <Text
              fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
              color="white"
              fontFamily="'Carmen Sans Regular', sans-serif"
              fontWeight="normal"
              letterSpacing="0.05em"
              className="slide-in-left"
            >
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            </Text>

            {/* Main Heading */}
            <Heading
              as="h1"
              fontSize={{ base: "4xl", md: "5xl", lg: "6xl" }}
              fontWeight="bold"
              color="white"
              fontFamily="'Carmen Sans Bold', sans-serif"
              lineHeight="1.15"
              maxW={{ base: "100%", md: "900px", lg: "1000px" }}
              className="slide-in-left-delay-1"
            >
              Get in touch
            </Heading>
          </VStack>
        </Box>
      </Box>

      {/* Contact Form Section */}
      <Box
        as="section"
        w="full"
        py={{ base: 16, md: 20 }}
        px={{ base: 8, md: 16, lg: 20 }}
        bg="white"
        mt="-80px"
        position="relative"
        zIndex={20}
        borderRadius="3xl 3xl 0 0"
        boxShadow="0 -20px 40px rgba(0,0,0,0.05)"
      >
        <Box
          px={{ base: 6, md: 12, lg: 16 }}
          w="full"
        >
          <Box
            display="flex"
            flexDirection={{ base: "column", lg: "row" }}
            gap={{ base: 8, lg: 12 }}
            w="full"
          >
          {/* Contact Form Container */}
          <Box
            flex="1"
            bg="white"
            p={{ base: 6, md: 8 }}
            borderRadius="md"
            className="has_ae_slider elementor-element elementor-element-79b75cec e-con-full e-flex ae-bg-gallery-type-default e-con e-child"
          >
            <form onSubmit={handleSubmit} style={{ width: "100%" }}>
              <VStack spacing={6} align="stretch">

                {/* Name Fields Row */}
                <Box display="flex" flexDirection={{ base: "column", lg: "row" }} gap={6}>
                  <Box flex="1">
                    <Text
                      fontSize="sm"
                      color="gray.500"
                      mb={2}
                      textTransform="uppercase"
                      letterSpacing="1px"
                      fontFamily="'Carmen Sans Light', sans-serif"
                    >
                      FIRST NAME
                    </Text>
                    <Box
                      borderBottom="1px solid"
                      borderColor="gray.300"
                      pb={2}
                    >
                      <input
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                        style={{
                          width: "100%",
                          border: "none",
                          outline: "none",
                          background: "transparent",
                          fontSize: "16px",
                          fontFamily: "'Carmen Sans Light', sans-serif",
                        }}
                      />
                    </Box>
                  </Box>
                  <Box flex="1">
                    <Text
                      fontSize="sm"
                      color="gray.500"
                      mb={2}
                      textTransform="uppercase"
                      letterSpacing="1px"
                      fontFamily="'Carmen Sans Light', sans-serif"
                    >
                      LAST NAME
                    </Text>
                    <Box
                      borderBottom="1px solid"
                      borderColor="gray.300"
                      pb={2}
                    >
                      <input
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                        style={{
                          width: "100%",
                          border: "none",
                          outline: "none",
                          background: "transparent",
                          fontSize: "16px",
                          fontFamily: "'Carmen Sans Light', sans-serif",
                        }}
                      />
                    </Box>
                  </Box>
                </Box>

                {/* Email and Phone Row */}
                <Box display="flex" flexDirection={{ base: "column", lg: "row" }} gap={6}>
                  <Box flex="1">
                    <Text
                      fontSize="sm"
                      color="gray.500"
                      mb={2}
                      textTransform="uppercase"
                      letterSpacing="1px"
                      fontFamily="'Carmen Sans Light', sans-serif"
                    >
                      YOUR EMAIL
                    </Text>
                    <Box
                      borderBottom="1px solid"
                      borderColor="gray.300"
                      pb={2}
                    >
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        style={{
                          width: "100%",
                          border: "none",
                          outline: "none",
                          background: "transparent",
                          fontSize: "16px",
                          fontFamily: "'Carmen Sans Light', sans-serif",
                        }}
                      />
                    </Box>
                  </Box>
                  <Box flex="1">
                    <Text
                      fontSize="sm"
                      color="gray.500"
                      mb={2}
                      textTransform="uppercase"
                      letterSpacing="1px"
                      fontFamily="'Carmen Sans Light', sans-serif"
                    >
                      YOUR PHONE
                    </Text>
                    <Box
                      borderBottom="1px solid"
                      borderColor="gray.300"
                      pb={2}
                    >
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        inputMode="tel"
                        pattern="[0-9+\s-]*"
                        style={{
                          width: "100%",
                          border: "none",
                          outline: "none",
                          background: "transparent",
                          fontSize: "16px",
                          fontFamily: "'Carmen Sans Light', sans-serif",
                        }}
                      />
                    </Box>
                  </Box>
                </Box>

                {/* Message Textarea */}
                <Box>
                  <Text
                    fontSize="sm"
                    color="gray.500"
                    mb={2}
                    textTransform="uppercase"
                    letterSpacing="1px"
                    fontFamily="'Carmen Sans Light', sans-serif"
                  >
                    HOW MAY WE ASSIST
                  </Text>
                  <Box
                    borderBottom="1px solid"
                    borderColor="gray.300"
                    pb={2}
                  >
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={4}
                      style={{
                        width: "100%",
                        border: "none",
                        outline: "none",
                        background: "transparent",
                        fontSize: "16px",
                        fontFamily: "'Carmen Sans Light', sans-serif",
                        resize: "vertical",
                        minHeight: "100px",
                      }}
                    />
                  </Box>
                </Box>

                {/* Submit Button */}
                <Box>
                  <Box
                    as="button"
                    type="submit"
                    bg="black"
                    color="white"
                    w="full"
                    py={3}
                    borderRadius="md"
                    fontSize="sm"
                    fontWeight="bold"
                    textTransform="uppercase"
                    cursor="pointer"
                    transition="all 0.3s ease"
                    _hover={{ bg: "gray.800" }}
                    disabled={isSubmitting}
                    opacity={isSubmitting ? 0.6 : 1}
                  >
                    {isSubmitting ? "SUBMITTING..." : "SUBMIT"}
                  </Box>
                </Box>
              </VStack>
            </form>
          </Box>

          {/* Contact Information Container */}
          <Box
            flex={{ base: "1", lg: "0.5" }}
            className="has_ae_slider elementor-element elementor-element-6f8a17c e-con-full e-flex ae-bg-gallery-type-default e-con e-child"
            alignSelf="center"
            pl={{ base: 0, lg: 8 }}
          >
            <VStack spacing={4} align="stretch" justify="center" h="full">
              {/* Contact Us Section */}
              <Box>
                <Heading
                  as="h3"
                  fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                  fontWeight="bold"
                  color={textColor}
                  mb={6}
                  lineHeight="1.2"
                  fontFamily="'Carmen Sans Bold', sans-serif"
                >
                  Contact Us To Get More Information
                </Heading>
                <Text
                  fontSize={{ base: "sm", md: "md" }}
                  color={textColor}
                  fontFamily="'Carmen Sans Light', sans-serif"
                  lineHeight="1.6"
                  fontWeight="300"
                  opacity={0.8}
                  maxW={{ base: "100%", lg: "400px" }}
                  mb={8}
                >
                  Mercurian Equity is an investment management firm, focused on delivering investment solutions and strategic capital partnerships for global institutions and wholesale clients.
                </Text>
              </Box>

              {/* Address Section */}
              <Box>
                <Box display="flex" alignItems="flex-start" mb={4}>
                  <Box
                    as="svg"
                    w="20px"
                    h="20px"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#3182ce"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    mr={3}
                    flexShrink={0}
                    mt={1}
                  >
                    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                    <circle cx="12" cy="10" r="3" />
                  </Box>
                  <Text fontSize={{ base: "sm", md: "md" }} color={textColor} lineHeight="1.6" fontFamily="'Carmen Sans Light', sans-serif" fontWeight="300" opacity={0.8}>
                    Level 28, 140 St Georges Terrace, Perth, Western Australia, 6000
                  </Text>
                </Box>
              </Box>

              {/* Email Section */}
              <Box>
                <Box display="flex" alignItems="center">
                  <Box
                    as="svg"
                    w="20px"
                    h="20px"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#3182ce"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    mr={3}
                    flexShrink={0}
                  >
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </Box>
                  <Text
                    as="a"
                    href="mailto:info@mercurianequity.com"
                    fontSize={{ base: "sm", md: "md" }}
                    color={textColor}
                    fontFamily="'Carmen Sans Light', sans-serif"
                    fontWeight="300"
                    opacity={0.8}
                    _hover={{ textDecoration: "underline" }}
                  >
                    info@mercurianequity.com
                  </Text>
                </Box>
              </Box>
            </VStack>
          </Box>
          </Box>
        </Box>
      </Box>

      <GreshamFooter />

      {/* Submission Success Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent mx={4}>
          <ModalHeader
            fontSize="2xl"
            fontWeight="bold"
            fontFamily="'Carmen Sans Bold', sans-serif"
            pt={8}
            pb={4}
            textAlign="center"
          >
            Thank you
          </ModalHeader>
          <ModalBody pb={6} px={8}>
            <VStack spacing={4} align="center">
              <Text
                fontSize="lg"
                textAlign="center"
                color="gray.700"
                fontFamily="'Carmen Sans Light', sans-serif"
                lineHeight="1.6"
              >
                Your message has been successfully received. A member of our
                team will be in contact with you shortly.
              </Text>
              
            </VStack>
          </ModalBody>
          <ModalFooter justifyContent="center" pb={8}>
            <Button
              onClick={onClose}
              bg="black"
              color="white"
              size="lg"
              px={12}
              py={6}
              fontSize="md"
              fontWeight="bold"
              textTransform="uppercase"
              _hover={{ bg: "gray.800" }}
              fontFamily="'Carmen Sans Regular', sans-serif"
            >
              OK
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Contact;